import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, HostListener, signal, computed, effect, inject, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil, firstValueFrom, filter } from 'rxjs';

import { LanguageService } from '@core/i18n/language.service';
import {
  InfrastructureCatalogItem as Infrastructure,
  InfrastructureCatalogService,
  InfrastructureSelectionService,
  NetworkCatalogItem as Network,
  NetworkCatalogService,
} from '@core/catalogs';

interface StrSelectorState {
  isLoading: boolean;
  error: string | null;
  networks: Network[];
  infrastructures: Infrastructure[];
}

const ROUTES_WITH_STR_ID: Record<string, number> = {
  document: 1,
  monitor: 1,
  'infrastructure': 1,
  'resources/infrastructure': 1
} as const;

@Component({
  selector: 'app-str-header-selector',
  imports: [FormsModule],
  templateUrl: './str-header-selector.component.html',
  styleUrl: './str-header-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrHeaderSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly infraCatalog = inject(InfrastructureCatalogService);
  private readonly networkCatalog = inject(NetworkCatalogService);
  private readonly infraSelection = inject(InfrastructureSelectionService);
  private readonly langService = inject(LanguageService);
  private readonly router = inject(Router);

  readonly isOpen = signal(false);
  readonly searchTerm = signal('');
  readonly selectedInfrastructure = signal<Infrastructure | null>(null);
  readonly expandedNetworks = signal(new Set<number>());
  readonly currentUrl = signal(this.router.url);

  readonly state = signal<StrSelectorState>({
    isLoading: false,
    error: null,
    networks: [],
    infrastructures: []
  });

  readonly translateData = signal<Record<string, string>>({});

  readonly filteredNetworks = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const networks = this.state().networks;

    if (!term) {
      return networks;
    }

    return networks
      .map(network => ({
        ...network,
        Infrastructures: network.Infrastructures.filter(infra =>
          infra.hasAccess !== false && this.matchesSearch(infra, term)
        )
      }))
      .filter(network =>
        network.net_name.toLowerCase().includes(term) ||
        network.Infrastructures.length > 0
      );
  });

  readonly filteredInfrastructures = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const infrastructures = this.state().infrastructures;
    if (!term) {
      return infrastructures.filter(infra => infra.hasAccess !== false);
    }

    return infrastructures.filter(infra => infra.hasAccess !== false && this.matchesSearch(infra, term));
  });

  readonly selectedDisplayText = computed(() => {
    const infra = this.selectedInfrastructure();

    if (!infra) {
      return this.translateData()['select_infrastructure'] || 'Select Infrastructure';
    }

    return `${this.getCategoryText(infra)} ${(infra.str_name || '').toUpperCase()} ${this.getInfrastructureCodesText(infra)}`.trim();
  });

  readonly hasNoResults = computed(() => {
    return this.filteredNetworks().length === 0 &&
           this.filteredInfrastructures().length === 0;
  });

  readonly isVisible = computed(() => {
    const segments = this.currentUrl().split('/').filter(Boolean);
    const onInfraProfile = segments[0] === 'resources' && segments[1] === 'infrastructure';

    if (!onInfraProfile) return true;

    const { isLoading, infrastructures, networks } = this.state();
    if (isLoading) return true;

    const infraId = segments[2];
    if (!infraId || infraId === 'create') return true;

    return !!this.findInfrastructureById(infraId, infrastructures, networks);
  });

  constructor() {
    effect(() => {
      const term = this.searchTerm();
      if (term && this.filteredNetworks().length > 0) {
        const expanded = new Set<number>();
        this.filteredNetworks().forEach((_, index) => {
          expanded.add(index);
        });
        this.expandedNetworks.set(expanded);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.langService.getSharedDictionary('str-header-selector')
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => this.translateData.set(data));

    await this.loadData();
    this.initializeSelection();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.currentUrl.set((event as NavigationEnd).urlAfterRedirects);
      this.syncSelectionFromUrl();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.str-selector');

    if (!dropdown && this.isOpen()) {
      this.closeDropdown();
    }
  }

  toggleDropdown(): void {
    this.isOpen.update(value => !value);

    if (this.isOpen()) {
      document.body.classList.add('str-selector-open');
      this.resetDropdownState();
      setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
    } else {
      document.body.classList.remove('str-selector-open');
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    document.body.classList.remove('str-selector-open');
    this.resetDropdownState();
  }

  toggleNetwork(index: number): void {
    this.expandedNetworks.update(networks => {
      const newSet = new Set(networks);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }

  isNetworkExpanded(index: number): boolean {
    return this.expandedNetworks().has(index);
  }

  selectInfrastructure(infrastructure: Infrastructure): void {
    this.selectedInfrastructure.set(infrastructure);
    this.infraSelection.select(infrastructure.str_id);
    this.navigateToInfrastructure(infrastructure.str_id);

    this.closeDropdown();
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  private async loadData(): Promise<void> {
    this.state.update(state => ({ ...state, isLoading: true, error: null }));
    try {
      const dictionary = await firstValueFrom(
        this.langService.getCommonDictionary().pipe(takeUntil(this.destroy$))
      );

      const [infrastructuresResponse, networksResponse] = await Promise.all([
        this.infraCatalog.loadAll(dictionary),
        this.networkCatalog.loadForCurrentUser(dictionary)
      ]);

      const seenIds = new Set<string>();

      const sortByName = (a: Infrastructure, b: Infrastructure) =>
        (a.str_name || '').localeCompare(b.str_name || '');

      const validInfrastructures = (infrastructuresResponse || [])
        .filter((infra: Infrastructure) => {
          if (!infra.str_id || infra.str_id.trim() === '' || !infra.str_name || seenIds.has(infra.str_id) || infra.hasAccess === false) {
            return false;
          }
          seenIds.add(infra.str_id);
          return true;
        })
        .sort(sortByName);

      const validNetworks = (networksResponse || []).map((network: Network) => ({
        ...network,
        Infrastructures: network.Infrastructures.filter((infra: Infrastructure) => {
          if (!infra.str_id || infra.str_id.trim() === '' || !infra.str_name || seenIds.has(infra.str_id) || infra.hasAccess === false) {
            return false;
          }
          seenIds.add(infra.str_id);
          return true;
        }).sort(sortByName)
      })).filter((network: Network) => network.Infrastructures.length > 0);

      this.state.update(state => ({
        ...state,
        isLoading: false,
        networks: validNetworks,
        infrastructures: validInfrastructures
      }));

    } catch (error) {
      console.error('Error loading infrastructure data:', error);

      this.state.update(state => ({
        ...state,
        isLoading: false,
        error: 'Failed to load infrastructure data',
        networks: [],
        infrastructures: []
      }));
    }
  }

  private initializeSelection(): void {
    const { networks, infrastructures } = this.state();

    const urlCode = this.getCodeFromUrl();
    if (urlCode) {
      const found = this.findInfrastructureById(urlCode, infrastructures, networks);
      if (found) {
        this.selectedInfrastructure.set(found);
        this.infraSelection.select(found.str_id);
        return;
      }
      if (this.isInfrastructureProfilePage()) {
        return;
      }
    }

    const savedStrId = this.infraSelection.selectedStrId();
    if (savedStrId) {
      const found = this.findInfrastructureById(savedStrId, infrastructures, networks);
      if (found) {
        this.selectedInfrastructure.set(found);
        return;
      }
    }
    const firstInfra = infrastructures.find(i => !i.isPetitioned) || networks.find(n => n.Infrastructures.some(i => !i.isPetitioned))?.Infrastructures.find(i => !i.isPetitioned);
    if (firstInfra) {
      this.selectInfrastructure(firstInfra);
    }
  }

  private syncSelectionFromUrl(): void {
    const urlCode = this.getCodeFromUrl();
    if (!urlCode) return;

    if (this.selectedInfrastructure()?.str_id === urlCode) return;

    const { networks, infrastructures } = this.state();
    if (!infrastructures.length && !networks.length) return;
    const found = this.findInfrastructureById(urlCode, infrastructures, networks);
    if (found) {
      this.selectedInfrastructure.set(found);
      this.infraSelection.select(found.str_id);
    }
  }

  private getCodeFromUrl(): string | null {
    const urlSegments = this.router.url.split('/').filter(Boolean);
    if (!urlSegments.length) return null;

    if (urlSegments.length >= 2) {
      const compoundPrefix = `${urlSegments[0]}/${urlSegments[1]}`;
      if (compoundPrefix in ROUTES_WITH_STR_ID) {
        const position = ROUTES_WITH_STR_ID[compoundPrefix] + 1;
        return urlSegments[position] ?? null;
      }
    }

    const prefix = urlSegments[0];
    if (!prefix || !(prefix in ROUTES_WITH_STR_ID)) return null;
    return urlSegments[ROUTES_WITH_STR_ID[prefix]] ?? null;
  }

  private isInfrastructureProfilePage(): boolean {
    const segments = this.router.url.split('/').filter(Boolean);
    return segments[0] === 'resources' && segments[1] === 'infrastructure';
  }

  private findInfrastructureById(
    strId: string,
    infrastructures: Infrastructure[],
    networks: Network[]
  ): Infrastructure | undefined {
    let found = infrastructures.find(infra => infra.str_id === strId);
    if (!found) {
      for (const network of networks) {
        found = network.Infrastructures.find(infra => infra.str_id === strId);
        if (found) break;
      }
    }

    return found;
  }

  private matchesSearch(infrastructure: Infrastructure, searchTerm: string): boolean {
    const searchLower = searchTerm.toLowerCase();

    return [
      infrastructure.str_id,
      infrastructure.str_denom,
      infrastructure.str_iata,
      infrastructure.str_icao,
      infrastructure.str_local
    ].some(field =>
      field && field.toLowerCase().includes(searchLower)
    );
  }

  getCategoryText(infrastructure: Infrastructure): string {
    const cat = infrastructure.category;
    if (!Array.isArray(cat) || cat.length === 0) return '';
    return cat.map((s: any) => s.cat_name || '').filter(Boolean).join(', ');
  }

  getInfrastructureCodesText(infrastructure: Infrastructure): string {
    const normalize = (value: string | undefined | null) => {
      const trimmed = value?.trim();
      return trimmed && trimmed !== '-' ? trimmed : '-';
    };

    return `[ ${normalize(infrastructure.str_icao)} / ${normalize(infrastructure.str_iata)} / ${normalize(infrastructure.str_local)} / & ${normalize(infrastructure.str_id)} ]`;
  }

  private navigateToInfrastructure(strId: string): void {
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(Boolean);

    const prefix = urlSegments[0]==='resources'? urlSegments[0]+'/'+urlSegments[1] : urlSegments[0];

    if (!prefix || !(prefix in ROUTES_WITH_STR_ID)) {
      return;
    }

    const position = ROUTES_WITH_STR_ID[prefix];
    const newSegments = this.buildNewSegments(urlSegments, strId, position);

    this.router.navigate(newSegments, { replaceUrl: true })
      .catch(error => {
        console.error('Navigation error:', error);
      });
  }

  private buildNewSegments(
    currentSegments: string[],
    strId: string,
    position: number
  ): string[] {
    if (currentSegments.length > position) {
      const newSegments = [...currentSegments];
      const t=currentSegments[0]==='resources'? position+1:position;
      newSegments[t] = strId;
      return newSegments;
    }

    return [currentSegments[0], strId];
  }

  private resetDropdownState(): void {
    this.searchTerm.set('');
    this.expandedNetworks.set(new Set());
  }
}
